import { Card, CardContent, CardHeader, CardTitle } from '@components/ui/Card';
import { Loader2, Lock } from 'lucide-react';
import { MODULLER } from '@lib/modules';
import { useModulAyarlari, useUpdateModulAyari } from './hooks/useModulAyar';
import { useAuthStore } from '@/store/auth';

interface Props {
  /** Modal içinde kullanılırken Card wrapper'ı atla. */
  embedded?: boolean;
}

/**
 * Modül Sihirbazı — kullanıcı hangi modülleri kullanacağını seçer.
 * Kapatılan modül sidebar'da görünmez, route'u da gizli olur.
 */
export function ModulSihirbaziKart({ embedded }: Props = {}) {
  const { data, isLoading } = useModulAyarlari();
  const update = useUpdateModulAyari();
  // Modül açıp kapamak firma ayarı: yalnız ayar yetkisi olan (sahip/yönetici) değiştirir
  const degistirebilir = useAuthStore((s) => s.hasIzin('ayarlar:guncelle'));

  const liste = (
    <>
      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Yükleniyor…
        </div>
      ) : (
        <ul className="space-y-2">
          {MODULLER.map((m) => {
            const ayar = data?.find((a) => a.modul === m.key);
            const aktif = ayar?.aktif ?? true;
            const Icon = m.icon;
            return (
              <li
                key={m.key}
                className="flex items-center justify-between rounded-md border p-3"
              >
                <div className="flex items-center gap-3">
                  <div className="rounded bg-muted p-2 text-muted-foreground">
                    <Icon className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">{m.label}</p>
                    <p className="text-xs text-muted-foreground">{m.to}</p>
                  </div>
                </div>
                {m.zorunlu ? (
                  <span className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Lock className="h-3 w-3" /> Zorunlu
                  </span>
                ) : (
                  <label className="relative inline-flex cursor-pointer items-center">
                    <input
                      type="checkbox"
                      className="peer sr-only"
                      checked={aktif}
                      disabled={update.isPending || !degistirebilir}
                      aria-label={`${m.label} modülünü ${aktif ? 'kapat' : 'aç'}`}
                      onChange={(e) =>
                        update.mutate({ modul: m.key, aktif: e.target.checked })
                      }
                    />
                    <div className="h-6 w-11 rounded-full bg-muted-foreground/30 transition-colors after:absolute after:left-0.5 after:top-0.5 after:h-5 after:w-5 after:rounded-full after:bg-white after:transition-all peer-checked:bg-primary peer-checked:after:translate-x-5" />
                  </label>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {!isLoading && !degistirebilir && (
        <p className="mt-3 text-xs text-muted-foreground">Modülleri yalnız firma sahibi ya da yönetici açıp kapatabilir.</p>
      )}
    </>
  );

  if (embedded) return liste;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Modül Sihirbazı</CardTitle>
        <p className="text-xs text-muted-foreground">
          Kullanmadığın modülleri kapatabilirsin. Kapalı modüller sol menüde görünmez.
        </p>
      </CardHeader>
      <CardContent>{liste}</CardContent>
    </Card>
  );
}
